if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton =
          this.querySelector('[type="submit"]');

        this.submitButtonText =
          this.submitButton?.querySelector('.tst-atc-text') ||
          this.submitButton?.querySelector('span');

        if (
          this.submitButton &&
          document.querySelector('cart-drawer')
        ) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }

        this.hideErrors =
          this.dataset.hideErrors === 'true';
      }

      getAddonConfig() {
        const configId =
          this.dataset.tstAddonConfigId;

        if (!configId) return null;

        return document.getElementById(
          configId
        );
      }

      getAddonRows() {
        const config =
          this.getAddonConfig();

        if (!config) return [];

        return Array.from(
          config.querySelectorAll(
            '[data-tst-cart-addon]'
          )
        );
      }

      getFormProperties(formData) {
        const properties = {};

        for (
          const [key, value]
          of formData.entries()
        ) {
          const match =
            String(key).match(
              /^properties\[(.*)\]$/
            );

          if (
            !match ||
            value instanceof File ||
            String(value).trim() === ''
          ) {
            continue;
          }

          properties[match[1]] =
            String(value);
        }

        return properties;
      }

      isAddonCartItem(item) {
        const properties =
          item?.properties || {};

        return Boolean(
          properties._tst_cart_addon ||
          properties._tst_cart_addon_parent ||
          properties._tst_cart_addon_variant_id
        );
      }

      async getCurrentCart() {
        const rootUrl =
          window.Shopify?.routes?.root ||
          '/';

        const response = await fetch(
          `${rootUrl}cart.js`,
          {
            headers: {
              Accept:
                'application/json',
              'X-Requested-With':
                'XMLHttpRequest',
            },
            cache: 'no-store',
          }
        );

        if (!response.ok) {
          throw new Error(
            'Unable to read cart.'
          );
        }

        return response.json();
      }

      async buildAddonPayload(
        formData
      ) {
        const config =
          this.getAddonConfig();

        const addonRows =
          this.getAddonRows();

        if (
          !config ||
          addonRows.length === 0
        ) {
          return null;
        }

        const mainToken =
          config.dataset.mainToken || '';

        const mainVariantId =
          Number(
            formData.get('id') || 0
          );

        const mainQuantity =
          Math.max(
            1,
            Number.parseInt(
              formData.get(
                'quantity'
              ) || '1',
              10
            ) || 1
          );

        if (
          !mainVariantId ||
          !mainToken
        ) {
          return null;
        }

        const mainProperties =
          this.getFormProperties(
            formData
          );

        mainProperties
          ._tst_cart_addon_main =
          mainToken;

        addonRows.forEach(
          (row) => {
            const index =
              row.dataset.index;

            if (
              !index ||
              !row.dataset.variantId
            ) {
              return;
            }

            mainProperties[
              `_tst_addon_${index}_variant_id`
            ] =
              row.dataset.variantId;

            mainProperties[
              `_tst_addon_${index}_title`
            ] =
              row.dataset.title || '';

            mainProperties[
              `_tst_addon_${index}_description`
            ] =
              row.dataset.description ||
              '';

            mainProperties[
              `_tst_addon_${index}_image`
            ] =
              row.dataset.image || '';

            mainProperties[
              `_tst_addon_${index}_price`
            ] =
              row.dataset.price || '0';

            mainProperties[
              `_tst_addon_${index}_compare_price`
            ] =
              row.dataset
                .comparePrice || '0';

            mainProperties[
              `_tst_addon_${index}_url`
            ] =
              row.dataset.url || '';

            mainProperties[
              `_tst_addon_${index}_available`
            ] =
              row.dataset.available ||
              'false';
          }
        );

        const mainItem = {
          id: mainVariantId,
          quantity: mainQuantity,
          properties:
            mainProperties,
        };

        const sellingPlan =
          formData.get(
            'selling_plan'
          );

        if (sellingPlan) {
          mainItem.selling_plan =
            Number(sellingPlan) ||
            sellingPlan;
        }

        const items = [
          mainItem,
        ];

        let currentCart = {
          items: [],
        };

        try {
          currentCart =
            await this.getCurrentCart();
        } catch (error) {
          console.warn(
            '[TST cart add-ons] Unable to check existing cart:',
            error
          );
        }

        const existingAddonVariants =
          new Set();

        currentCart.items.forEach(
          (item) => {
            if (
              !this.isAddonCartItem(
                item
              )
            ) {
              return;
            }

            const variantId =
              Number(
                item.variant_id ||
                item.id ||
                0
              );

            if (variantId) {
              existingAddonVariants.add(
                variantId
              );
            }
          }
        );

        addonRows.forEach(
          (row) => {
            const index =
              Number(
                row.dataset.index ||
                0
              );

            const variantId =
              Number(
                row.dataset
                  .variantId || 0
              );

            const available =
              row.dataset.available ===
              'true';

            const selectedByDefault =
              row.dataset
                .defaultSelected !==
              'false';

            if (
              !index ||
              !variantId ||
              !available ||
              !selectedByDefault
            ) {
              return;
            }

            if (
              existingAddonVariants.has(
                variantId
              )
            ) {
              return;
            }

            existingAddonVariants.add(
              variantId
            );

            items.push({
              id: variantId,
              quantity: 1,
              properties: {
                _tst_cart_addon:
                  'shared',
                _tst_cart_addon_variant_id:
                  String(
                    variantId
                  ),
              },
            });
          }
        );

        return {
          items,
          mainVariantId,
        };
      }

      async onSubmitHandler(
        evt
      ) {
        evt.preventDefault();

        if (
          !this.submitButton ||
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add(
          'loading'
        );

        const spinner =
          this.querySelector(
            '.loading__spinner'
          );

        if (spinner) {
          spinner.classList.remove(
            'hidden'
          );
        }

        const config =
          fetchConfig(
            'javascript'
          );

        config.headers[
          'X-Requested-With'
        ] =
          'XMLHttpRequest';

        const formData =
          new FormData(
            this.form
          );

        let addonPayload = null;

        try {
          addonPayload =
            await this.buildAddonPayload(
              formData
            );
        } catch (error) {
          console.error(
            '[TST cart add-ons] Build payload failed:',
            error
          );
        }

        const productVariantId =
          formData.get('id');

        if (this.cart) {
          this.cart.setActiveElement(
            document.activeElement
          );
        }

        if (addonPayload) {
          config.headers[
            'Content-Type'
          ] =
            'application/json';

          config.body =
            JSON.stringify({
              items:
                addonPayload.items,

              sections:
                this.cart
                  ? this.cart
                      .getSectionsToRender()
                      .map(
                        (section) =>
                          section.id
                      )
                  : [],

              sections_url:
                window.location
                  .pathname,
            });
        } else {
          delete config.headers[
            'Content-Type'
          ];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart
                .getSectionsToRender()
                .map(
                  (section) =>
                    section.id
                )
            );

            formData.append(
              'sections_url',
              window.location
                .pathname
            );
          }

          config.body =
            formData;
        }

        fetch(
          `${routes.cart_add_url}`,
          config
        )
          .then(
            (response) =>
              response.json()
          )
          .then(
            (response) => {
              if (
                response.status
              ) {
                publish(
                  PUB_SUB_EVENTS
                    .cartError,
                  {
                    source:
                      'product-form',

                    productVariantId,

                    errors:
                      response.errors ||
                      response.description,

                    message:
                      response.message,
                  }
                );

                this.handleErrorMessage(
                  response.description
                );

                const soldOutMessage =
                  this.submitButton.querySelector(
                    '.sold-out-message'
                  );

                if (
                  !soldOutMessage
                ) {
                  return;
                }

                this.submitButton.setAttribute(
                  'aria-disabled',
                  true
                );

                if (
                  this.submitButtonText
                ) {
                  this.submitButtonText.classList.add(
                    'hidden'
                  );
                }

                soldOutMessage.classList.remove(
                  'hidden'
                );

                this.error =
                  true;

                return;
              }

              if (!this.cart) {
                window.location =
                  window.routes
                    .cart_url;

                return;
              }

              const startMarker =
                CartPerformance.createStartingMarker(
                  'add:wait-for-subscribers'
                );

              if (!this.error) {
                publish(
                  PUB_SUB_EVENTS
                    .cartUpdate,
                  {
                    source:
                      'product-form',

                    productVariantId,

                    cartData:
                      response,
                  }
                ).then(() => {
                  CartPerformance.measureFromMarker(
                    'add:wait-for-subscribers',
                    startMarker
                  );
                });
              }

              this.error =
                false;

              const quickAddModal =
                this.closest(
                  'quick-add-modal'
                );

              if (
                quickAddModal
              ) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(
                      () => {
                        CartPerformance.measure(
                          'add:paint-updated-sections',
                          () => {
                            this.cart.renderContents(
                              response
                            );
                          }
                        );
                      }
                    );
                  },
                  {
                    once: true,
                  }
                );

                quickAddModal.hide(
                  true
                );
              } else {
                CartPerformance.measure(
                  'add:paint-updated-sections',
                  () => {
                    this.cart.renderContents(
                      response
                    );
                  }
                );
              }
            }
          )
          .catch(
            (error) => {
              console.error(
                error
              );

              this.handleErrorMessage(
                'Unable to add to cart. Please try again.'
              );
            }
          )
          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains(
                'is-empty'
              )
            ) {
              this.cart.classList.remove(
                'is-empty'
              );
            }

            if (!this.error) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            const spinner =
              this.querySelector(
                '.loading__spinner'
              );

            if (spinner) {
              spinner.classList.add(
                'hidden'
              );
            }

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );
          });
      }

      handleErrorMessage(
        errorMessage = false
      ) {
        if (
          this.hideErrors
        ) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (
          !this
            .errorMessageWrapper
        ) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (
          !this.submitButton
        ) {
          return;
        }

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (
            text &&
            this.submitButtonText
          ) {
            this.submitButtonText.textContent =
              text;
          }

          return;
        }

        this.submitButton.removeAttribute(
          'disabled'
        );

        if (
          !this
            .submitButtonText
        ) {
          return;
        }

        const customTextEnabled =
          this.submitButtonText
            ?.dataset
            ?.enableCustomAtcText ===
          'true';

        const customText =
          this.submitButtonText
            ?.dataset
            ?.customAtcText
            ?.trim();

        if (
          customTextEnabled &&
          customText
        ) {
          this.submitButtonText.textContent =
            customText;
        } else {
          this.submitButtonText.textContent =
            window.variantStrings
              .addToCart;
        }
      }

      get variantIdInput() {
        return this.form?.querySelector(
          '[name=id]'
        );
      }
    }
  );
}